import { assertNotEmpty } from '@festina-life/core'
import { FPlusOverflownCodeIndicator } from '@festina-life/fplus'
import { Tooltip } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Card, CardBody } from '@festina-life/ui/structure'
import { useMemo } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import type {
  EnumeratedValueFrameFieldValue,
  RangedValueFrameFieldValue,
  SimpleTemplateField,
} from '../../types/templateModel'
import { sortEnumOptionsWithOptionalDotPrefix } from '../fplusExpression/expressionType'
import type { ExpressionTypeInformation } from '../fplusExpression/types'
import { useFieldDocumentation } from '../templateFieldDocumentation/useFieldDocumentation'
import { TemplateFieldExpression } from '../templateFieldExpression/TemplateFieldExpression'
import { useFieldFrame } from '../templateFieldFrame/useFieldFrame'
import { FieldFramesValueDetails } from './FieldFramesValueDetails'

type Props = {
  field: SimpleTemplateField
  isSingleCellContent: boolean
}

export const TemplateFieldFramesCellContent = ({
  field,
  isSingleCellContent,
}: Props) => {
  const { templateVersion } = useTemplateContext()
  const translate = useTranslate()
  const {
    frame,
    shouldRenderAFrame,
    shouldRenderEnumeratedValueFrame,
    shouldRenderRangedValueFrameExpression,
  } = useFieldFrame(field)
  const { values } = useFieldDocumentation(field)
  const enumTypeInformation = useMemo(
    () =>
      shouldRenderEnumeratedValueFrame
        ? ({
            literalType: 'enum',
            collectionType: 'set',
            enumOptions: sortEnumOptionsWithOptionalDotPrefix({
              options: values ?? [],
            }),
          } as ExpressionTypeInformation)
        : undefined,
    [shouldRenderEnumeratedValueFrame, values],
  )
  assertNotEmpty(templateVersion)

  const shouldRenderSingleFPlusExpression =
    isSingleCellContent && shouldRenderAFrame

  if (shouldRenderSingleFPlusExpression) {
    //Render a single frame
    return (
      <>
        {shouldRenderEnumeratedValueFrame && (
          <TemplateFieldExpression
            expression={
              (frame as EnumeratedValueFrameFieldValue).entries?.expression
            }
            isExpanded={false}
            path={`${field.path}.entries`}
            fallbackCode=""
            typeInformation={enumTypeInformation}
            fromOrigin={
              (frame as EnumeratedValueFrameFieldValue).entries?.origin
            }
          />
        )}
        {shouldRenderRangedValueFrameExpression && (
          <TemplateFieldExpression
            expression={(frame as RangedValueFrameFieldValue).range?.expression}
            isExpanded={false}
            path={`${field.path}.range`}
            fallbackCode=""
            fromOrigin={(frame as RangedValueFrameFieldValue).range?.origin}
          />
        )}
      </>
    )
  }

  return shouldRenderAFrame && !!frame ? (
    <div className="flex size-full flex-1">
      <Tooltip
        tip={
          <Card>
            <CardBody>
              <FieldFramesValueDetails
                frame={frame}
                path={field.path}
                enumTypeInformation={enumTypeInformation}
              />
            </CardBody>
          </Card>
        }
        className="h-full grow"
      >
        <div className="flex size-full cursor-help items-center justify-center">
          <FPlusOverflownCodeIndicator
            title={translate('templateViewer.frameColumn.framesTooltip')}
          />
        </div>
      </Tooltip>
    </div>
  ) : null
}
